import {
  onBeforeMount,
  onBeforeUnmount,
  provide,
  inject,
  ref,
  type InjectionKey,
  type Ref
} from 'vue'
import { emitter } from '@/plugins/emitter'

export const MAX_EDUCATIONAL_HISTORIES = 4
export const MAX_SKILLS = 10
export const MAX_SKILL_LENGTH = 30
export const MAX_LICENSE_HISTORIES = 10
export const MAX_LICENSE_LENGTH = 30
export const MAX_PR_MESSAGE_LENGTH = 350
export const descriptionMaxLength = 80

type EnglishResumeHistoryItem = {
  year: number | ''
  month: number | ''
  description: string
  uuid: string
}

type EnglishResumeProjectHistoryItem = {
  projectTitle: string
  startYear: number | ''
  startMonth: number | ''
  endYear: number | ''
  endMonth: number | ''
  contentsOfProject: string
  envAndScale: string
  position: string
  uuid: string
}

type EnglishResumeJobHistoryItem = {
  companyName: string
  startYear: number | ''
  startMonth: number | ''
  endYear: number | ''
  endMonth: number | ''
  isContinuing: boolean
  businessArea: string
  projects: EnglishResumeProjectHistoryItem[]
  uuid: string
}

export type EnglishResumeFormatData = {
  createDate: Date | null
  website: string
  recentPosition: string
  address: string
  educationalHistories: EnglishResumeHistoryItem[]
  skills: string[]
  licenseHistories: EnglishResumeHistoryItem[]
  jobHistories: EnglishResumeJobHistoryItem[]
  prMessage: string
} & Record<string, unknown>

type HistoryColumns = {
  year?: number | string
  month?: number | string
  description?: string
}

type JobHistoryColumns = {
  companyName?: string
  startYear?: number | string
  startMonth?: number | string
  endYear?: number | string
  endMonth?: number | string
  isContinuing?: boolean
  businessArea?: string
  projects?: EnglishResumeProjectHistoryItem[]
}

type ProjectHistoryColumns = {
  projectTitle?: string
  startYear?: number | string
  startMonth?: number | string
  endYear?: number | string
  endMonth?: number | string
  contentsOfProject?: string
  envAndScale?: string
  position?: string
}

const DEACTIVATE_HISTORY_ITEM = 'deactivate-history-item'

const ENGLISH_RESUME_FORMAT_DATA_KEY: InjectionKey<EnglishResumeFormatData> =
  Symbol('englishResumeFormatData')

function uuidv4(): string {
  const cryptoObj =
    window.crypto || (window as Window & { msCrypto?: Crypto }).msCrypto
  const template = '10000000-1000-4000-8000-100000000000'
  return template.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (cryptoObj!.getRandomValues(new Uint8Array(1))[0] &
        (15 >> (Number(c) / 4)))
    ).toString(16)
  )
}

function createHistoryItem(columns?: HistoryColumns): EnglishResumeHistoryItem {
  const cols = columns || {}
  return {
    year: Number(cols.year) || '',
    month: Number(cols.month) || '',
    description: cols.description || '',
    uuid: uuidv4()
  }
}

function createJobHistoryItem(
  columns?: JobHistoryColumns
): EnglishResumeJobHistoryItem {
  const cols = columns || {}
  return {
    companyName: cols.companyName || '',
    startYear: Number(cols.startYear) || '',
    startMonth: Number(cols.startMonth) || '',
    endYear: Number(cols.endYear) || '',
    endMonth: Number(cols.endMonth) || '',
    isContinuing: cols.isContinuing || false,
    businessArea: cols.businessArea || '',
    projects: cols.projects || [],
    uuid: uuidv4()
  }
}

function createProjectHistoryItem(
  columns?: ProjectHistoryColumns
): EnglishResumeProjectHistoryItem {
  const cols = columns || {}
  return {
    projectTitle: cols.projectTitle || '',
    startYear: Number(cols.startYear) || '',
    startMonth: Number(cols.startMonth) || '',
    endYear: Number(cols.endYear) || '',
    endMonth: Number(cols.endMonth) || '',
    contentsOfProject: cols.contentsOfProject || '',
    envAndScale: cols.envAndScale || '',
    position: cols.position || '',
    uuid: uuidv4()
  }
}

function moveHistory<T>(
  historyList: T[],
  params: { index: number; to: string }
) {
  const fromIndex = params.index
  const to = params.to
  if (fromIndex === 0 && to === 'up') {
    return
  }
  if (fromIndex + 1 === historyList.length && to === 'down') {
    return
  }
  if (to === 'up') {
    historyList.splice(
      fromIndex - 1,
      2,
      historyList[fromIndex],
      historyList[fromIndex - 1]
    )
  } else if (to === 'down') {
    historyList.splice(
      fromIndex,
      2,
      historyList[fromIndex + 1],
      historyList[fromIndex]
    )
  }
}

export function createEmptyEnglishResumeFormatData(): EnglishResumeFormatData {
  return {
    createDate: null,
    website: '',
    recentPosition: '',
    address: '',
    educationalHistories: [],
    skills: [],
    licenseHistories: [],
    jobHistories: [],
    prMessage: ''
  }
}

export function provideEnglishResumeFormatData(
  formatData: EnglishResumeFormatData
) {
  provide(ENGLISH_RESUME_FORMAT_DATA_KEY, formatData)
}

export function useEnglishResumeFormatData(): EnglishResumeFormatData {
  const formatData = inject(ENGLISH_RESUME_FORMAT_DATA_KEY)
  if (!formatData) {
    throw new Error(
      'useEnglishResumeFormatData() must be used under EnglishResumeEdit'
    )
  }
  return formatData
}

export type EnglishResumeHistories = {
  activeHistoryItem: Ref<string>
  addEducationalHistory: (
    index?: number | null,
    columns?: HistoryColumns
  ) => void
  deleteEducationalHistory: (index: number) => void
  moveEducationalHistory: (params: { index: number; to: string }) => void
  addLicenseHistory: (index?: number | null, columns?: HistoryColumns) => void
  deleteLicenseHistory: (index: number) => void
  moveLicenseHistory: (params: { index: number; to: string }) => void
  addSkill: () => void
  deleteSkill: (index: number) => void
  moveSkill: (index: number, to: string) => void
  addJobHistory: (index?: number | null, columns?: JobHistoryColumns) => void
  deleteJobHistory: (index: number) => void
  moveJobHistory: (params: { index: number; to: string }) => void
  addProjectHistory: (index: number, columns?: ProjectHistoryColumns) => void
  deleteHistoryDetail: (index: number, index2: number) => void
  moveJobHistoryDetail: (
    params: { index: number; to: string },
    parentIndex: number
  ) => void
  activateHistoryItem: (id: string) => void
  deactivateHistoryItem: () => void
}

const ENGLISH_RESUME_HISTORIES_KEY: InjectionKey<EnglishResumeHistories> =
  Symbol('englishResumeHistories')

export function provideEnglishResumeHistories(
  histories: EnglishResumeHistories
) {
  provide(ENGLISH_RESUME_HISTORIES_KEY, histories)
}

export function useEnglishResumeHistoriesApi(): EnglishResumeHistories {
  const histories = inject(ENGLISH_RESUME_HISTORIES_KEY)
  if (!histories) {
    throw new Error(
      'useEnglishResumeHistoriesApi() must be used under EnglishResumeEdit'
    )
  }
  return histories
}

export function useEnglishResumeHistories(
  formatData: EnglishResumeFormatData
): EnglishResumeHistories {
  const activeHistoryItem = ref('')

  function addEducationalHistory(
    index?: number | null,
    columns?: HistoryColumns
  ) {
    const historyList = formatData.educationalHistories
    if (historyList.length >= MAX_EDUCATIONAL_HISTORIES) return
    const newHistoryItem = createHistoryItem(columns)
    if (typeof index === 'number') {
      historyList.splice(index, 0, newHistoryItem)
    } else {
      historyList.push(newHistoryItem)
    }
  }

  function deleteEducationalHistory(index: number) {
    formatData.educationalHistories.splice(index, 1)
  }

  function moveEducationalHistory(params: { index: number; to: string }) {
    moveHistory(formatData.educationalHistories, params)
  }

  function addLicenseHistory(index?: number | null, columns?: HistoryColumns) {
    const historyList = formatData.licenseHistories
    if (historyList.length >= MAX_LICENSE_HISTORIES) return
    const newHistoryItem = createHistoryItem(columns)
    if (typeof index === 'number') {
      historyList.splice(index, 0, newHistoryItem)
    } else {
      historyList.push(newHistoryItem)
    }
  }

  function deleteLicenseHistory(index: number) {
    formatData.licenseHistories.splice(index, 1)
  }

  function moveLicenseHistory(params: { index: number; to: string }) {
    moveHistory(formatData.licenseHistories, params)
  }

  function addSkill() {
    if (formatData.skills.length >= MAX_SKILLS) return
    formatData.skills.push('')
  }

  function deleteSkill(index: number) {
    formatData.skills.splice(index, 1)
  }

  function moveSkill(index: number, to: string) {
    const list = formatData.skills
    if (to === 'up' && index > 0) {
      list.splice(index - 1, 2, list[index], list[index - 1])
    } else if (to === 'down' && index + 1 < list.length) {
      list.splice(index, 2, list[index + 1], list[index])
    }
  }

  function addJobHistory(index?: number | null, columns?: JobHistoryColumns) {
    const historyList = formatData.jobHistories
    const newHistoryItem = createJobHistoryItem(columns)
    if (typeof index === 'number') {
      historyList.splice(index, 0, newHistoryItem)
    } else {
      historyList.push(newHistoryItem)
    }
  }

  function deleteJobHistory(index: number) {
    formatData.jobHistories.splice(index, 1)
  }

  function moveJobHistory(params: { index: number; to: string }) {
    moveHistory(formatData.jobHistories, params)
  }

  function addProjectHistory(index: number, columns?: ProjectHistoryColumns) {
    const targetItem = formatData.jobHistories[index]
    const newHistoryItem = createProjectHistoryItem(columns)
    targetItem.projects.push(newHistoryItem)
  }

  function deleteHistoryDetail(index: number, index2: number) {
    formatData.jobHistories[index].projects.splice(index2, 1)
  }

  function moveJobHistoryDetail(
    params: { index: number; to: string },
    parentIndex: number
  ) {
    moveHistory(formatData.jobHistories[parentIndex].projects, params)
  }

  function activateHistoryItem(id: string) {
    activeHistoryItem.value = id
  }

  function deactivateHistoryItem() {
    activeHistoryItem.value = ''
  }

  onBeforeMount(() => {
    emitter.on(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  onBeforeUnmount(() => {
    emitter.off(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  return {
    activeHistoryItem,
    addEducationalHistory,
    deleteEducationalHistory,
    moveEducationalHistory,
    addLicenseHistory,
    deleteLicenseHistory,
    moveLicenseHistory,
    addSkill,
    deleteSkill,
    moveSkill,
    addJobHistory,
    deleteJobHistory,
    moveJobHistory,
    addProjectHistory,
    deleteHistoryDetail,
    moveJobHistoryDetail,
    activateHistoryItem,
    deactivateHistoryItem
  }
}
